import React from "react";
import { Clock, X } from "lucide-react";

import EntityAvatar from "components/Entities/EntityAvatar";
import type {
  RecentSearchEntry,
  SearchResult,
  TrendingSearchGroups,
} from "types/search";
import type { SearchLocale } from "./searchCopy";
import { getSearchCopy } from "./searchCopy";
import SearchResultItem from "./SearchResultItem";
import { recentIssueToIssueEntity, type RecentIssue } from "util/recentIssuesUtils";
import { capitalizeFirstLetter } from "util/textUtils";

interface Props {
  locale: SearchLocale;
  recentSearches: RecentSearchEntry[];
  recentIssues: RecentIssue[];
  trendingGroups: TrendingSearchGroups;
  onRemoveRecentSearch: (entryId: string) => void;
  onRemoveRecentIssue: (issueId: number) => void;
  onSelectRecentSearch: (entry: RecentSearchEntry) => void;
  onSelectRecentIssue: (issue: RecentIssue) => void;
  onSeeAllRecentSearches: () => void;
  onSeeAllRecentIssues: () => void;
  onSeeAllTrendingSection: (section: "issues" | "posts" | "tags") => void;
  onSelectTrendingResult: (result: SearchResult) => void;
}

const TRENDING_SECTIONS: Array<{
  id: keyof TrendingSearchGroups;
  labelKey: "issues" | "posts" | "tags";
}> = [
  { id: "issues", labelKey: "issues" },
  { id: "posts", labelKey: "posts" },
  { id: "tags", labelKey: "tags" },
];

const SearchDiscoveryPanel: React.FC<Props> = ({
  locale,
  recentSearches,
  recentIssues,
  trendingGroups,
  onRemoveRecentSearch,
  onRemoveRecentIssue,
  onSelectRecentSearch,
  onSelectRecentIssue,
  onSeeAllRecentSearches,
  onSeeAllRecentIssues,
  onSeeAllTrendingSection,
  onSelectTrendingResult,
}) => {
  const hasRecent = recentSearches.length > 0 || recentIssues.length > 0;
  const hasTrending = TRENDING_SECTIONS.some(
    (section) => trendingGroups[section.id].length > 0
  );

  return (
    <>
      {hasRecent ? (
        <>
          <li className="px-2 pt-2 pb-1 text-left">
            <span className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
              {getSearchCopy(locale, "recent")}
            </span>
          </li>

          {recentSearches.length > 0 ? (
            <>
              <li className="px-4 py-1 text-left">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-eoe-text-meta dark:text-eoe-dark-text-meta">
                    {capitalizeFirstLetter(getSearchCopy(locale, "searches"))}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                    onClick={onSeeAllRecentSearches}
                  >
                    {getSearchCopy(locale, "seeAll")}
                  </button>
                </div>
              </li>
              {recentSearches.slice(0, 6).map((entry) => (
                <li
                  key={entry.id}
                  className="group px-2 py-2 hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon cursor-pointer text-eoe-text dark:text-eoe-dark-text flex justify-between items-center"
                >
                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left"
                    onClick={() => onSelectRecentSearch(entry)}
                  >
                    <span className="w-8 h-8 flex-shrink-0 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon flex items-center justify-center">
                      <Clock className="size-4" />
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate">{entry.query}</span>
                      <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {capitalizeFirstLetter(getSearchCopy(
                          locale,
                          entry.scope === "actors" ? "people" : entry.scope
                        ))}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="mr-2 flex h-5 w-5 flex-shrink-0 items-center justify-center text-eoe-text-sub dark:text-eoe-dark-text-sub"
                    onClick={() => onRemoveRecentSearch(entry.id)}
                    aria-label={getSearchCopy(locale, "removeRecent")}
                  >
                    <X className="size-4" />
                  </button>
                </li>
              ))}
            </>
          ) : null}

          {recentIssues.length > 0 ? (
            <>
              <li className="px-4 py-1 text-left">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-eoe-text-meta dark:text-eoe-dark-text-meta">
                    {capitalizeFirstLetter(getSearchCopy(locale, "issues"))}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                    onClick={onSeeAllRecentIssues}
                  >
                    {getSearchCopy(locale, "seeAll")}
                  </button>
                </div>
              </li>
              {recentIssues.slice(0, 6).map((recentIssue) => (
                <li
                  key={`recent-issue-${recentIssue.id}`}
                  className="group px-2 py-2 hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon cursor-pointer text-eoe-text dark:text-eoe-dark-text"
                  onClick={() => onSelectRecentIssue(recentIssue)}
                >
                  <EntityAvatar
                    entity={recentIssueToIssueEntity(recentIssue)}
                    entityType="issue"
                    entityId={recentIssue.id}
                    entityChip={true}
                    showCloseChip={true}
                    onEntityChipClose={() => onRemoveRecentIssue(recentIssue.id)}
                    showLink={false}
                    className="text-xs pr-0"
                  />
                </li>
              ))}
            </>
          ) : null}
        </>
      ) : null}

      {hasTrending ? (
        <>
          <li className="px-2 pt-2 pb-1 text-left">
            <span className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
              {getSearchCopy(locale, "trending")}
            </span>
          </li>
          {TRENDING_SECTIONS.map((section) =>
            trendingGroups[section.id].length > 0 ? (
              <React.Fragment key={section.id}>
                <li className="px-4 py-1 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-eoe-text-meta dark:text-eoe-dark-text-meta">
                      {capitalizeFirstLetter(getSearchCopy(locale, section.labelKey))}
                    </span>
                    <button
                      type="button"
                      className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                      onClick={() => onSeeAllTrendingSection(section.id)}
                    >
                      {getSearchCopy(locale, "seeAll")}
                    </button>
                  </div>
                </li>
                {trendingGroups[section.id].map((result) => (
                  <SearchResultItem
                    key={result.id}
                    result={result}
                    locale={locale}
                    compact
                    onSelect={onSelectTrendingResult}
                  />
                ))}
              </React.Fragment>
            ) : null
          )}
        </>
      ) : null}
    </>
  );
};

export default SearchDiscoveryPanel;
